import { useState, useEffect } from 'react';
import { BarChart3, Clock, Wallet, AlertTriangle, TrendingUp, Target, DollarSign, Activity } from 'lucide-react';

// Mock agency data
const MOCK_AGENCY = {
  name: 'Nexus Digital Agency',
  id: 'AGY-0042',
  tier: 'Gold',
  sla_target: 150, // blocks per week
  current_week_sales: 87,
  pending_escrow_usdt: 12450.00,
  cleared_payouts_usdt: 84200.00,
  master_link: 'https://gaurilabs.com/ref/nexus-0042',
  sla_weeks_missed: 1,
  compound_active: true,
};

function useCountdown() {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const sunday = new Date(now);
      sunday.setDate(now.getDate() + (7 - now.getDay()));
      sunday.setHours(23, 59, 59, 0);
      const diff = sunday - now;
      if (diff <= 0) { setTimeLeft('00:00:00:00'); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${d}d ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, []);

  return timeLeft;
}

export default function AffiliatePanel() {
  const [agency] = useState(MOCK_AGENCY);
  const countdown = useCountdown();

  const progressPct = Math.min(100, (agency.current_week_sales / agency.sla_target) * 100);
  const runRate = agency.current_week_sales; // Current week progress
  const projectedEnd = Math.round(runRate * (7 / Math.max(1, new Date().getDay() || 7)));
  const isMissing = projectedEnd < agency.sla_target;

  return (
    <div className="space-y-6" data-testid="affiliate-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-mono text-white tracking-tight flex items-center gap-3">
            <BarChart3 className="w-7 h-7 text-[#06b6d4] drop-shadow-[0_0_8px_#06b6d4]" />
            SLA PERFORMANCE MATRIX
          </h1>
          <p className="text-sm font-mono text-[#64748b] mt-1">
            Agency: <span className="text-[#06b6d4]">{agency.name}</span> | ID: <span className="text-[#eab308]">{agency.id}</span>
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-mono text-[#64748b] uppercase tracking-widest">Week Deadline</p>
          <p className="text-xl font-mono font-bold text-[#f97316] drop-shadow-[0_0_6px_#f97316]" data-testid="countdown-timer">{countdown}</p>
        </div>
      </div>

      {/* SLA Penalty Warning */}
      {isMissing && agency.compound_active && (
        <div className="p-4 rounded-xl border-2 border-[#f97316] bg-[#f97316]/5 animate-pulse" data-testid="sla-penalty-warning"
          style={{ boxShadow: '0 0 20px rgba(249, 115, 22, 0.15), inset 0 0 20px rgba(249, 115, 22, 0.05)' }}>
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-[#f97316] flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-mono font-bold text-[#f97316] text-sm">2-WEEK SLA COMPOUND ACTIVE</p>
              <p className="text-xs font-mono text-[#f97316]/80 mt-1">
                Current run-rate projects <span className="font-bold text-white">{projectedEnd}</span> blocks vs target <span className="font-bold text-white">{agency.sla_target}</span>.
                Missing target triggers <span className="text-[#f97316] font-bold">10% Liquid Penalty</span> on fallback blocks.
              </p>
              <p className="text-[10px] font-mono text-[#64748b] mt-2">
                Consecutive misses: {agency.sla_weeks_missed} week(s) | Compound multiplier escalating
              </p>
            </div>
          </div>
        </div>
      )}

      {/* KPI Engine - Progress Bar */}
      <div className="p-6 rounded-xl border border-[#1e293b] bg-[#0B0F19]" data-testid="kpi-engine">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Target className="w-5 h-5 text-[#06b6d4]" />
            <p className="text-sm font-mono font-bold text-white">WEEKLY SLA BLOCK TARGET</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs font-mono text-[#64748b]">
              <span className="text-white font-bold text-lg">{agency.current_week_sales}</span> / {agency.sla_target} blocks
            </span>
            <span className={`px-2 py-1 rounded text-[10px] font-mono font-bold ${
              progressPct >= 80 ? 'bg-[#06b6d4]/10 text-[#06b6d4]' :
              progressPct >= 50 ? 'bg-[#eab308]/10 text-[#eab308]' :
              'bg-[#f97316]/10 text-[#f97316]'
            }`}>{progressPct.toFixed(1)}%</span>
          </div>
        </div>

        {/* Massive Progress Bar */}
        <div className="h-8 rounded-full bg-[#1e293b] overflow-hidden relative" data-testid="progress-bar">
          <div
            className="h-full rounded-full transition-all duration-1000 relative"
            style={{
              width: `${progressPct}%`,
              background: progressPct >= 80 ? 'linear-gradient(90deg, #06b6d4, #22d3ee)' :
                          progressPct >= 50 ? 'linear-gradient(90deg, #eab308, #facc15)' :
                          'linear-gradient(90deg, #f97316, #fb923c)',
              boxShadow: progressPct >= 80 ? '0 0 20px #06b6d4' :
                         progressPct >= 50 ? '0 0 20px #eab308' :
                         '0 0 20px #f97316',
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/10 animate-pulse" />
          </div>
          {/* Target line */}
          <div className="absolute top-0 bottom-0 w-0.5 bg-white/50" style={{ left: '100%' }} />
        </div>

        <div className="flex items-center justify-between mt-3 text-xs font-mono text-[#64748b]">
          <span>Week start: {new Date(Date.now() - new Date().getDay() * 86400000).toLocaleDateString()}</span>
          <span>Projected: ~{projectedEnd} blocks</span>
          <span className="text-[#f97316]">Sunday 23:59 UTC</span>
        </div>
      </div>

      {/* Escrow Ledger Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="escrow-ledger">
        <div className="p-6 rounded-xl border border-[#1e293b] bg-[#0B0F19] relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#eab308]/5 rounded-full blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5 text-[#eab308]" />
              <p className="text-xs font-mono text-[#64748b] uppercase tracking-widest">Pending Escrow</p>
            </div>
            <p className="text-4xl font-mono font-bold text-white" data-testid="pending-escrow">
              ${agency.pending_escrow_usdt.toLocaleString()}
              <span className="text-sm text-[#eab308] ml-2">USDT</span>
            </p>
            <p className="text-xs font-mono text-[#64748b] mt-2">Held until SLA verification complete</p>
          </div>
        </div>

        <div className="p-6 rounded-xl border border-[#1e293b] bg-[#0B0F19] relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#06b6d4]/5 rounded-full blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="w-5 h-5 text-[#06b6d4]" />
              <p className="text-xs font-mono text-[#64748b] uppercase tracking-widest">Cleared Payouts</p>
            </div>
            <p className="text-4xl font-mono font-bold text-white" data-testid="cleared-payouts">
              ${agency.cleared_payouts_usdt.toLocaleString()}
              <span className="text-sm text-[#06b6d4] ml-2">USDT</span>
            </p>
            <p className="text-xs font-mono text-[#64748b] mt-2">Total lifetime cleared earnings</p>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Run Rate', value: `${(agency.current_week_sales / Math.max(1, new Date().getDay() || 7)).toFixed(1)}/day`, color: '#06b6d4' },
          { label: 'SLA Status', value: isMissing ? 'AT RISK' : 'ON TRACK', color: isMissing ? '#f97316' : '#06b6d4' },
          { label: 'Tier', value: agency.tier, color: '#eab308' },
          { label: 'Penalty Risk', value: agency.compound_active ? 'COMPOUND' : 'NONE', color: agency.compound_active ? '#f97316' : '#06b6d4' },
        ].map((m, i) => (
          <div key={i} className="p-4 rounded-xl border border-[#1e293b] bg-[#0B0F19]">
            <p className="text-[10px] font-mono text-[#64748b] uppercase tracking-widest">{m.label}</p>
            <p className="text-lg font-mono font-bold mt-1" style={{ color: m.color }}>{m.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
